import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAudio } from '../contexts/AudioContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Check, PlayCircle, Star, Sparkles, ChevronRight, Lock, X } from 'lucide-react';
import AudioPlayer from '../components/AudioPlayer';
import GamificationWidget from '../components/GamificationWidget';
import { toast } from 'sonner';
import { useAccessControl } from '../hooks/useAccessControl';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHECKIN_EMOJIS = [
  { emoji: '\u{1F60C}', label: 'Serein(e)' },
  { emoji: '\u{1F624}', label: 'Agac\u00e9(e)' },
  { emoji: '\u{1F614}', label: 'Triste' },
  { emoji: '\u{1F634}', label: 'Fatigu\u00e9(e)' },
  { emoji: '\u{1F60A}', label: 'Content(e)' },
];

export const Programme = () => {
  const { token } = useAuth();
  const { currentAudio, isBuffering, loadAudio } = useAudio();
  const { isTrial, canAccessTheme } = useAccessControl();
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [allCompleted, setAllCompleted] = useState(false);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [challenge, setChallenge] = useState(null);

  useEffect(() => {
    fetchThemes();
    fetchCheckin();
    fetchChallenge();
  }, []);

  const fetchCheckin = async () => {
    try {
      const res = await axios.get(`${API_URL}/checkin/today`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.emoji) setTodayCheckin(res.data.emoji);
    } catch {}
  };

  const saveCheckin = async (emoji) => {
    try {
      await axios.post(`${API_URL}/checkin`, { emoji }, { headers: { Authorization: `Bearer ${token}` } });
      setTodayCheckin(emoji);
    } catch { toast.error('Erreur'); }
  };

  const fetchChallenge = async () => {
    try {
      const res = await axios.get(`${API_URL}/challenge/current`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.data.dismissed) setChallenge(res.data);
    } catch {}
  };

  const dismissChallenge = async () => {
    try {
      await axios.post(`${API_URL}/challenge/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setChallenge(null);
    } catch {}
  };

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThemes(response.data);
      
      // Vérifier si tous les thèmes sont complétés
      const allDone = response.data.every(theme => 
        theme.progress?.zen?.completed && theme.progress?.movement?.completed
      );
      setAllCompleted(allDone);
    } catch (error) {
      console.error('Erreur chargement thèmes:', error);
      toast.error('Erreur lors du chargement des thèmes');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAudio = async (theme, mode) => {
    setSelectedTheme(theme);
    setSelectedMode(mode);
    toast('Chargement de la séance en cours...', { duration: 10000, id: 'audio-loading' });

    const success = await loadAudio(theme, mode);
    toast.dismiss('audio-loading');
    if (!success) {
      toast.error('Erreur lors du chargement de l\'audio');
    }
  };

  const isAudioCompleted = (theme, mode) => {
    return theme.progress?.[mode]?.completed;
  };

  const bothModesCompleted = (theme) => {
    return isAudioCompleted(theme, 'zen') && isAudioCompleted(theme, 'movement');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-sage-700 font-sans">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" data-testid="programme-page">
      {/* Header */}
      <div className="bg-gradient-to-b from-sage-100 to-sand-50 px-6 py-8">
        <h1 className="text-4xl font-serif font-light text-sage-700 mb-2" data-testid="programme-title">
          Programme
        </h1>
        <p className="text-sage-700/70 font-sans">
          22 séances d'hypnose pour transformer votre relation à l'alimentation
        </p>
      </div>

      {/* Widget Gamification */}
      <div className="px-6 py-4">
        <GamificationWidget />
      </div>

      {/* Check-in matinal */}
      <div className="px-6 pb-4">
        <div className="bg-white rounded-xl border border-sand-200 p-4" data-testid="checkin-widget">
          {todayCheckin ? (
            <div className="flex items-center gap-3">
              <span className="text-3xl">{todayCheckin}</span>
              <div>
                <p className="text-sage-700 font-medium text-sm">Check-in du jour</p>
                <p className="text-sage-400 text-xs">{CHECKIN_EMOJIS.find(e => e.emoji === todayCheckin)?.label || ''}</p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sage-700 font-medium text-sm mb-3">Comment allez-vous aujourd'hui ?</p>
              <div className="flex justify-between">
                {CHECKIN_EMOJIS.map(({ emoji, label }) => (
                  <button
                    key={emoji}
                    onClick={() => saveCheckin(emoji)}
                    className="flex flex-col items-center gap-1 p-2 rounded-xl hover:bg-sage-50 transition-colors"
                    data-testid={`checkin-${label}`}
                  >
                    <span className="text-2xl">{emoji}</span>
                    <span className="text-[10px] text-sage-400">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Défi hebdomadaire */}
      {challenge && (
        <div className="px-6 pb-4">
          <div className="bg-declic/5 border border-declic/20 rounded-xl p-4 flex items-start gap-3" data-testid="weekly-challenge">
            <span className="text-lg mt-0.5">🎯</span>
            <div className="flex-1">
              <p className="text-sage-700 text-sm font-medium leading-relaxed">{challenge.challenge}</p>
            </div>
            <button onClick={dismissChallenge} className="text-sage-400 hover:text-sage-600 flex-shrink-0 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Message compact si programme complété */}
      {allCompleted && (
        <div className="px-6 pb-4">
          <div className="bg-declic/5 rounded-xl p-4 border border-declic/20 flex items-center gap-3">
            <Star className="w-8 h-8 text-declic flex-shrink-0" fill="currentColor" />
            <div>
              <p className="text-sage-700 font-semibold text-sm">Félicitations !</p>
              <p className="text-sage-500 text-xs">Programme complet. Réécoutez les séances autant que vous le souhaitez.</p>
            </div>
          </div>
        </div>
      )}

      {/* Thèmes list */}
      <div className="px-6 py-6 space-y-4">
        {themes.map((theme) => {
          const isCompleted = bothModesCompleted(theme);
          const isRecommended = theme.is_recommended && !isCompleted;
          const zenCompleted = isAudioCompleted(theme, 'zen');
          const movementCompleted = isAudioCompleted(theme, 'movement');

          return (
            <div
              key={theme.id}
              className={`bg-white rounded-2xl border ${
                isRecommended ? 'border-declic border-2 shadow-md' : 'border-sand-200'
              } p-6 shadow-sm relative`}
              data-testid={`theme-card-${theme.slug}`}
            >
              {/* Badge recommandé */}
              {isRecommended && (
                <div className="absolute -top-3 left-4 bg-declic text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                  <ChevronRight className="w-3 h-3" />
                  Recommandé
                </div>
              )}
              
              {/* Theme header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-sans text-sage-700/60">
                      Séance {theme.index + 1}
                    </span>
                    {isCompleted && (
                      <Check className="w-5 h-5 text-sage-500" data-testid={`theme-completed-${theme.slug}`} />
                    )}
                  </div>
                  <h3 className="text-xl font-serif font-light text-sage-700">
                    {theme.title}
                  </h3>
                  {theme.description && (
                    <p className="text-sm text-sage-700/70 font-sans mt-2">
                      {theme.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Audio buttons */}
              {canAccessTheme(theme.slug) ? (
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handlePlayAudio(theme, 'zen')}
                    disabled={isBuffering}
                    variant="outline"
                    className={`h-20 flex flex-col items-center justify-center space-y-1 border-2 ${
                      zenCompleted
                        ? 'border-sage-500 bg-sage-50'
                        : 'border-sand-200 hover:border-sage-500'
                    }`}
                    data-testid={`theme-zen-button-${theme.slug}`}
                  >
                    <PlayCircle className="w-6 h-6 text-sage-500" />
                    <span className="text-sm font-sans text-sage-700">Mode Zen</span>
                    {theme.duration_zen > 0 && (
                      <span className="text-xs text-sage-500">
                        {Math.floor(theme.duration_zen / 60)}:{(theme.duration_zen % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                    {zenCompleted && (
                      <Check className="w-4 h-4 text-sage-500" />
                    )}
                  </Button>

                  <Button
                    onClick={() => handlePlayAudio(theme, 'movement')}
                    disabled={isBuffering}
                    variant="outline"
                    className={`h-20 flex flex-col items-center justify-center space-y-1 border-2 ${
                      movementCompleted
                        ? 'border-declic bg-declic/5'
                        : 'border-sand-200 hover:border-declic'
                    }`}
                    data-testid={`theme-movement-button-${theme.slug}`}
                  >
                    <PlayCircle className="w-6 h-6 text-declic" />
                    <span className="text-sm font-sans text-sage-700">Mode Mouvement</span>
                    {theme.duration_movement > 0 && (
                      <span className="text-xs text-declic">
                        {Math.floor(theme.duration_movement / 60)}:{(theme.duration_movement % 60).toString().padStart(2, '0')}
                      </span>
                    )}
                    {movementCompleted && (
                      <Check className="w-4 h-4 text-declic" />
                    )}
                  </Button>
                </div>
              ) : (
                <div className="bg-sage-50 rounded-xl p-4 text-center border border-sage-200">
                  <Lock className="w-5 h-5 text-sage-400 mx-auto mb-2" />
                  <p className="text-sage-500 text-xs mb-2">Disponible avec l'accès complet</p>
                  <button
                    onClick={() => window.location.href = '/'}
                    className="text-declic text-xs font-medium hover:underline"
                  >
                    Passer à l'accès complet
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Recommandation */}
      <div className="px-6 py-4">
        <div className="bg-sage-50 rounded-xl p-4 border border-sage-100">
          <p className="text-sm text-sage-700 font-sans leading-relaxed">
            <strong>✨ Conseil :</strong> Écoutez d'abord le mode Zen, puis le mode Mouvement (vous pouvez les espacer). Accordez-vous ensuite au moins 1 jour de pause avant de passer au thème suivant.
          </p>
        </div>
      </div>

      {/* Audio player */}
      <AudioPlayer />
    </div>
  );
};

export default Programme;